import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { CLINICS, whatsappUrl, consultMessage, SAME_PRICE_NOTE } from "../data/clinics";
import { lockBodyScroll } from "../lib/scrollLock";
import { WhatsApp, X, ChevronRight, MapPin } from "./Icons";

const ClinicPickerContext = createContext(null);

/**
 * Every WhatsApp CTA goes through openPicker(buildMessage), where buildMessage(clinic)
 * returns the prefilled text. With one clinic it opens WhatsApp directly; with more it
 * asks which clinic first, so the chat lands at the right branch.
 */
export function ClinicPickerProvider({ children }) {
  const [buildMessage, setBuildMessage] = useState(null);

  const openPicker = useCallback((fn) => {
    // Guard against being passed straight to onClick (fn would be the click event)
    const build = typeof fn === "function" ? fn : consultMessage;
    if (CLINICS.length === 1) {
      window.open(whatsappUrl(CLINICS[0], build(CLINICS[0])), "_blank", "noopener,noreferrer");
      return;
    }
    setBuildMessage(() => build);
  }, []);

  const close = useCallback(() => setBuildMessage(null), []);

  return (
    <ClinicPickerContext.Provider value={openPicker}>
      {children}
      {buildMessage && <ClinicPickerDialog buildMessage={buildMessage} onClose={close} />}
    </ClinicPickerContext.Provider>
  );
}

export function useClinicPicker() {
  const openPicker = useContext(ClinicPickerContext);
  if (!openPicker) throw new Error("useClinicPicker must be used inside ClinicPickerProvider");
  return openPicker;
}

function ClinicPickerDialog({ buildMessage, onClose }) {
  const panelRef = useRef(null);

  useEffect(() => {
    const unlock = lockBodyScroll();
    const previouslyFocused = document.activeElement;
    panelRef.current?.focus();

    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      unlock();
      window.removeEventListener("keydown", onKeyDown);
      previouslyFocused?.focus?.();
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center sm:p-6">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-primary/30 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />

      {/* Panel: bottom sheet on mobile, centered dialog on larger screens */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="clinic-picker-title"
        tabIndex={-1}
        className="relative w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-soft-xl
                   px-6 pt-8 pb-[max(1.5rem,env(safe-area-inset-bottom))] sm:p-8 animate-fade-up focus:outline-none"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-9 h-9 rounded-xl flex items-center justify-center text-text-muted
                     hover:text-primary hover:bg-primary/5 transition-colors cursor-pointer"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        <span className="section-label block mb-2">Book via WhatsApp</span>
        <h2 id="clinic-picker-title" className="font-display text-display-sm text-primary mb-2">
          Choose your <span className="text-accent-700">clinic</span>
        </h2>
        <p className="text-sm text-text-muted mb-6">
          We'll open a WhatsApp chat with the clinic you pick.
        </p>

        <ul className="space-y-3">
          {CLINICS.map((clinic) => (
            <li key={clinic.id}>
              <a
                href={whatsappUrl(clinic, buildMessage(clinic))}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onClose}
                className="group flex items-center gap-4 p-4 rounded-2xl border border-border/60
                           hover:border-primary/20 hover:bg-bg transition-all duration-200 cursor-pointer"
              >
                <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-accent/15 flex items-center justify-center">
                  <MapPin size={20} className="text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-primary">{clinic.name}</span>
                    {clinic.isNew && <span className="badge-accent py-0.5">New</span>}
                  </div>
                  <p className="text-xs text-text-muted mt-1 truncate">{clinic.address}</p>
                </div>
                <ChevronRight
                  size={18}
                  className="flex-shrink-0 text-text-light group-hover:text-primary group-hover:translate-x-0.5 transition-all"
                />
              </a>
            </li>
          ))}
        </ul>

        <p className="flex items-center justify-center gap-2 mt-6 text-xs text-text-muted">
          <WhatsApp size={14} className="text-[#25D366]" />
          {SAME_PRICE_NOTE}
        </p>
      </div>
    </div>
  );
}
